// deployment_class: customer_env_allowed
/**
 * UserMenu — avatar + dropdown for profile/settings/logout. Plan 14
 * §Task 6.
 */
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { useExclusiveMenu } from './useExclusiveMenu';
import { useHasScope } from '../auth/useHasScope';

function getInitials(fullName: string | null, email: string): string {
  if (fullName) {
    const parts = fullName.trim().split(/\s+/);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
    return (first + last).toUpperCase();
  }
  return email.slice(0, 2).toUpperCase();
}

export function UserMenu(): JSX.Element | null {
  const { user, status } = useAuth();
  // Org self-governance is shown only to a customer account admin. `admin:tenant_admin`
  // is the account_admin marker scope (Tier-2 gate · §4.1 capability-driven nav).
  const canAdminOrg = useHasScope('admin:tenant_admin');
  // Shared with the other top-bar menus so only one is ever open — and so this
  // one gains the Escape + outside-click close it never had. It previously shut
  // ONLY when one of its own items was clicked, which is why it stayed open
  // behind the Project menu.
  const { open, toggle, close, containerRef, triggerRef } = useExclusiveMenu('user');

  if (status !== 'authenticated' || !user) return null;

  const initials = getInitials(user.full_name, user.email);

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border border-rule bg-paper-soft hover:bg-paper-amber transition-colors"
      >
        {user.avatar_url ? (
          <img
            src={user.avatar_url}
            alt=""
            aria-hidden="true"
            className="w-7 h-7 rounded-full object-cover ring-1 ring-rule shrink-0"
          />
        ) : (
          <span
            aria-hidden="true"
            className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-trust text-paper text-body-xs font-semibold shrink-0"
          >
            {initials}
          </span>
        )}
        <span className="text-body-s font-semibold tracking-tight">{user.full_name ?? user.email}</span>
        <span className="text-warm-gray-d" aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <ul
          role="menu"
          className="absolute right-0 mt-1 min-w-[180px] bg-paper border border-rule shadow-md z-10"
        >
          <li role="menuitem">
            <Link
              to="/account/profile"
              onClick={close}
              className="block px-3 py-2 text-body-s hover:bg-paper-soft"
            >
              Profile
            </Link>
          </li>
          <li role="menuitem">
            <Link
              to="/account/preferences"
              onClick={close}
              className="block px-3 py-2 text-body-s hover:bg-paper-soft"
            >
              Settings
            </Link>
          </li>
          {canAdminOrg && (
            <li role="menuitem">
              <Link
                to="/account/organization"
                onClick={close}
                className="block px-3 py-2 text-body-s hover:bg-paper-soft"
              >
                Organization
              </Link>
            </li>
          )}
          <li role="menuitem">
            <Link
              to="/auth/logout"
              onClick={close}
              className="block px-3 py-2 text-body-s text-coral-d hover:bg-paper-soft"
            >
              Sign out
            </Link>
          </li>
        </ul>
      )}
    </div>
  );
}
